/**
 * Billing form registry (UI mirror of oppo_api/utils/billingForms.js) — the
 * single source of truth linking a service code's `type` to its USOR billing
 * form. Keep this in sync with the API copy.
 *
 * To make a new service code billable on an existing form, set its `type` (in
 * the Service Code editor) to one of the mapped values below.
 */

// The bespoke USOR forms. `key` matches the `documents.type` value.
export const BILLING_FORMS = {
  USOR95: {
    key: "USOR95",
    label: "USOR 95 - Job Coaching Tracker",
    producesInvoice: true,
    // Max admin-configurable clock-out questions (shift_note_questions) per
    // service code billed on this form — the form only has room for so much
    // answer text. null = unlimited.
    maxQuestions: 3,
  },
  USOR148: {
    key: "USOR148",
    label: "USOR 148 - Life Skills / Service Brokering / Supported Education",
    producesInvoice: true,
    maxQuestions: 2,
  },
  USOR96: {
    key: "USOR96",
    label: "USOR 96 - Job Development",
    producesInvoice: false,
    maxQuestions: 3,
  },
};

// Service-code `type` value -> billing form key. Matched case-insensitively.
export const SERVICE_TYPE_FORMS = {
  "Job Coaching": "USOR95",
  "Life Skills Coaching": "USOR148",
  "Job Development": "USOR96",
};

const norm = (s) => (s == null ? "" : String(s).trim().toLowerCase());

/** Returns the billing form definition for a service `type`, or null. */
export function getBillingFormForType(type) {
  const t = norm(type);
  for (const [mappedType, formKey] of Object.entries(SERVICE_TYPE_FORMS)) {
    if (norm(mappedType) === t) return BILLING_FORMS[formKey];
  }
  return null;
}

/** Returns the form key (e.g. 'USOR95') for a service `type`, or null. */
export function getFormKeyForType(type) {
  const f = getBillingFormForType(type);
  return f ? f.key : null;
}

/** True if the given service `type` maps to the given form key. */
export function typeMapsToForm(type, formKey) {
  return getFormKeyForType(type) === formKey;
}

/**
 * Max clock-out questions allowed per service code of the given `type`, or
 * null when the type is not billed on a USOR form (DSPD, blank) = unlimited.
 */
export function getMaxQuestionsForType(type) {
  const f = getBillingFormForType(type);
  return f && f.maxQuestions != null ? f.maxQuestions : null;
}

/**
 * True if the service `type` is a Job Development type (maps to USOR96).
 * Job Development codes are excluded from invoices. Mirrors the previous
 * hardcoded ['CE','SE','SJBT','TWE'] check; non-USOR types are not JD.
 */
export function isJobDevelopmentType(type) {
  return getFormKeyForType(type) === "USOR96";
}

/**
 * Options for the Service Code "Type" dropdown. Billable types are annotated
 * with their form; non-USOR options preserve DSPD / unset codes.
 */
export function getServiceTypeOptions() {
  const opts = Object.entries(SERVICE_TYPE_FORMS).map(([type, formKey]) => ({
    id: type,
    name: `${type} (${BILLING_FORMS[formKey].key.replace("USOR", "USOR ")})`,
  }));
  opts.push({ id: "DSPD Service", name: "DSPD Service (no USOR form)" });
  opts.push({ id: "", name: "None / Not USOR-billable" });
  return opts;
}
